import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../use-api.js';
import { useAuth } from '../auth.js';
import { apiFetch } from '../api-client.js';

interface Suppression {
  id: string;
  org: string;
  repo: string;
  rule: string;
  rationale: string;
  expires_at_epoch: number | null;
  created_at: string;
}

function fmtEpoch(epoch: number | null): string {
  if (epoch === null) return 'Never';
  return new Date(epoch * 1000).toLocaleDateString();
}

export function Suppressions() {
  const { idToken } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading, error } = useApiQuery<Suppression[]>(
    ['suppressions'],
    '/api/suppressions'
  );

  const extendMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/suppressions/${id}/extend`, idToken!, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ days: 30, rationale: 'extended via portal' })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries()
  });

  const sorted = [...(data ?? [])].sort((a, b) => {
    const ae = a.expires_at_epoch ?? Infinity;
    const be = b.expires_at_epoch ?? Infinity;
    return ae - be;
  });

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">Suppressions</h2>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {String(error)}</p>}

      {data && sorted.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-gray-400">
          <p className="text-sm">No active suppressions.</p>
        </div>
      )}

      {data && sorted.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded-lg text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium text-gray-600">Org</th>
                <th className="px-4 py-2 font-medium text-gray-600">Repo</th>
                <th className="px-4 py-2 font-medium text-gray-600">Rule</th>
                <th className="px-4 py-2 font-medium text-gray-600">Rationale</th>
                <th className="px-4 py-2 font-medium text-gray-600">Expires</th>
                <th className="px-4 py-2 font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{s.org}</td>
                  <td className="px-4 py-2 font-mono text-xs">{s.repo}</td>
                  <td className="px-4 py-2 font-mono text-xs">{s.rule}</td>
                  <td className="px-4 py-2 text-gray-600 max-w-xs truncate">{s.rationale}</td>
                  <td className="px-4 py-2">{fmtEpoch(s.expires_at_epoch)}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => extendMutation.mutate(s.id)}
                      disabled={extendMutation.isPending}
                      className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                    >
                      Extend 30d
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
